import type { CSSProperties } from 'styled-components';
import styled from 'styled-components';
import { Tooltip } from '@elfsight/widget-modal';
import { useTranslation } from 'react-i18next';
import type { PostSourceDisplayType } from '../../../types';
import { Badge, Link } from '../../lib/components';
import { useSocialFeedSettings } from '../../utils';
import { ShadowIcon } from './ShadowIcon';

export const SOURCE_ICON_SIZE = 24;

interface SourceLinkProps {
  url: string;
  show: boolean;
  display: PostSourceDisplayType;
  title: string;
  icon: string;
  sourceAlign?: CSSProperties['alignSelf'];
  color?: string;
  shadow?: boolean;
}

export function SourceLink({
  url,
  show,
  display,
  title,
  color,
  icon,
  sourceAlign = 'center',
  ...forwardingProps
}: SourceLinkProps) {
  const { tooltipBackgroundColor, tooltipTextColor } = useSocialFeedSettings();
  const { t } = useTranslation();

  if (!show || !display) {
    return null;
  }

  const translatedLabel = `${t('Posted on')} ${title}`;

  if (display === 'badge') {
    return (
      <StyledLink
        {...forwardingProps}
        href={url}
        aria-label={translatedLabel}
        hoverEffect="none"
        _sourceAlign={sourceAlign}
      >
        <Badge color={color}>{title}</Badge>
      </StyledLink>
    );
  }

  return (
    <Tooltip
      title={translatedLabel}
      background={tooltipBackgroundColor}
      fontColor={tooltipTextColor}
    >
      <StyledLink {...forwardingProps} href={url} aria-label={translatedLabel}>
        <ShadowIcon
          src={icon}
          alt={title}
          fill={color}
          size={SOURCE_ICON_SIZE}
        />
      </StyledLink>
    </Tooltip>
  );
}

const StyledLink = styled(Link)<{
  _sourceAlign?: CSSProperties['alignSelf'];
}>`
  align-self: ${({ _sourceAlign }) => _sourceAlign};
`;
